import * as yup from 'yup';
import { Validations } from '../../config';

export const getInputSchema = (text) => {
  const schemaEntries = text.reduce((acc, item) => {
    acc[item.value] = Validations.customText;
    return acc;
  });

  return yup.object().shape(schemaEntries);
};

export const generateValidationSchema = (fields) => {
  const schemaFields = {};
  const maxLengthMap = {};

  const panFields = new Set(['entity', 'pancard', 'pan', 'pan_number', 'panNumber']);
  const ifscFields = new Set(['ifsc_number', 'ifsc_code', 'ifsc', 'ifscNumber']);
  const sourcePincodeFields = new Set(['pincode1', 'sourcePinCode']);
  const destinationPincodeFields = new Set(['pincode2', 'distPinCode']);
  const dlFields = new Set(['dl_number', 'dlNumber']);
  const aadhaarFields = new Set(['aadhaar_number', 'aadhaar', 'aadhaarNumber']);
  const gstFields = new Set(['gst_number', 'gstNumber', 'gstnumber', 'gstin']);
  const vehicleFields = new Set(['vehicleNumber', 'vehicle_number']);
  const stringFields = new Set(['str1', 'str2', 'query', 'reference']);
  const epicFields = new Set(['epic_number', 'epicNumber']);

  const emailSchema = () =>
    yup
      .string()
      .required('Email is required')
      .max(254, 'Email is too long')
      .email('Invalid email format')
      .test('no-consecutive-dots', 'Email cannot contain consecutive dots', (value) => {
        return value ? !/\.{2,}/.test(value) : false;
      })
      .test('domain-dot-check', 'Email domain must contain a dot (e.g. example.com)', (value) => {
        if (!value) return false;
        const domain = value.split('@')[1]?.toLowerCase();
        return !!domain && domain.includes('.');
      })
      .test('valid-characters', 'Invalid characters in email', (value) => {
        return value
          ? /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(value)
          : false;
      })
      .test('gmail-domain-check', 'Gmail must end with .com, .co, or .in', (value) => {
        if (!value) return false;
        const domain = value.split('@')[1]?.toLowerCase();
        if (!domain) return false;

        if (domain.startsWith('gmail.')) {
          return ['com', 'co', 'in'].some((tld) => domain.endsWith(`.${tld}`));
        }
        return true;
      });

  const ipSchema = () =>
    yup
      .string()
      .required('IP Address is required')
      .test('ip-format', 'Invalid IPv4 or IPv6 Address format', (value) => {
        if (typeof value !== 'string') return false;

        const trimmedValue = value.trim();
        if (trimmedValue === '') return false;

        const ipv4Pattern = /^(?!0)(?!.*\.$)((25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.|$)){4}$/;
        const ipv6Pattern = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|(::)|([0-9a-fA-F]{1,4}::[0-9a-fA-F]{1,4}))$/;

        return ipv4Pattern.test(trimmedValue) || ipv6Pattern.test(trimmedValue);
      });

  const stringFieldSchema = (fieldValue) => {
    const requiredMessage =
      fieldValue === 'str1' || fieldValue === 'reference'
        ? 'String1 is required'
        : 'String2 is required';
    return yup
      .string()
      .required(requiredMessage)
      .matches(/^[A-Za-z ]{1,100}$/, 'Only alphabets allowed. No numbers or special characters.');
  };

  const epicSchema = (fieldValue) =>
    yup
      .string()
      .required('Voter ID Epic number is required')
      .matches(
        /^[A-Za-z0-9]{5,20}$/,
        fieldValue === 'epicNumber'
          ? 'Invalid Voter ID number format. Must be 5 to 20 alphanumeric digits.'
          : 'Invalid Voter ID Epic number'
      );

  const fieldRules = [
    { match: (v) => panFields.has(v), schema: () => yup.string().required('PAN Number is required').matches(/^[A-Za-z0-9]{10}$/, 'PAN Number must be 10 alphanumeric digits'), maxLength: 10 },
    { match: (v) => v === 'din_number', schema: () => yup.string().required('DIN Number is required').matches(/^[A-Za-z0-9]{10}$/, 'DIN Number must be 20 alphanumeric digits'), maxLength: 20 },
    { match: (v) => v === 'acc_number', schema: () => yup.string().required('Account number is required').matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]{5,20}$/, 'Invalid bank account number format. Must be 5 to 20 alphanumeric digits.'), maxLength: 20 },
    { match: (v) => ifscFields.has(v), schema: () => yup.string().required('IFSC code is required').matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]{11}$/, 'Invalid IFSC code format. Must be 11 alphanumeric digits.'), maxLength: 11 },
    { match: (v) => sourcePincodeFields.has(v), schema: () => yup.string().required('Source Pincode is required').matches(/^\d{6}$/, 'Invalid Source Pincode format. Must be 6 digits.'), maxLength: 6 },
    { match: (v) => destinationPincodeFields.has(v), schema: () => yup.string().required('Destination Pincode is required').matches(/^\d{6}$/, 'Invalid Destination Pincode format. Must be 6 digits.'), maxLength: 6 },
    { match: (v) => dlFields.has(v), schema: () => yup.string().required('Driving License Number is required').matches(/^[A-Za-z0-9]{5,20}$/, 'Driving License Number must be 5 to 20 alphanumeric digits'), maxLength: 20 },
    { match: (v) => aadhaarFields.has(v), schema: () => yup.string().required('Aadhar Number is required').matches(/^\d{12}$/, 'Aadhar Number must be exactly 12 numeric digits'), maxLength: 12 },
    { match: (v) => gstFields.has(v), schema: () => yup.string().required('GST Number is required').matches(/^[A-Za-z0-9]{15}$/, 'GST Number must be 15 alphanumeric digits'), maxLength: 15 },
    { match: (v) => v === 'mobile_number', schema: () => yup.string().required('Mobile Number is required').matches(/^\d{10}$/, 'Mobile Number must be exactly 10 numeric digits'), maxLength: 10 },
    { match: (v) => v === 'iec_number', schema: () => yup.string().required('ICE Number is required').matches(/^[A-Za-z0-9]{10}$/, 'ICE Number must be 10 alphanumeric digits'), maxLength: 10 },
    { match: (v) => vehicleFields.has(v), schema: () => yup.string().required('Vehicle Number is required').matches(/^[A-Za-z0-9]{8,12}$/, 'Invalid Vehicle number format. Must be 8 to 12 alphanumeric digits.'), maxLength: 12 },
    { match: (v) => stringFields.has(v), schema: stringFieldSchema, maxLength: 100 },
    { match: (v) => v === 'dinNumber', schema: () => yup.string().required('DIN Number is required').matches(/^\d{8}$/, 'DIN Number must be 8 digits'), maxLength: 8 },
    { match: (v) => epicFields.has(v), schema: epicSchema, maxLength: 20 },
    { match: (v) => v === 'engineNumber', schema: () => yup.string().required('Engine number is required').matches(/^[A-Za-z0-9]{5,20}$/, 'Invalid Engine number format. Must be 5 to 20 alphanumeric digits.'), maxLength: 20 },
    { match: (v) => v === 'chassisNumber', schema: () => yup.string().required('Chassis number is required').matches(/^[A-Za-z0-9]{5,20}$/, 'Invalid Chassis number format. Must be 5 to 20 alphanumeric digits.'), maxLength: 20 },
    { match: (v) => v === 'accNumber', schema: () => yup.string().required('Account number is required').matches(/^[A-Za-z0-9]{5,20}$/, 'Invalid bank account number format. Must be 5 to 20 alphanumeric characters.'), maxLength: 20 },
    { match: (v) => v === 'email', schema: emailSchema, maxLength: 254 },
    { match: (v) => v === 'ip', schema: ipSchema, maxLength: 45 },
  ];

  fields.forEach((field) => {
    const rule = fieldRules.find((r) => r.match(field.value));
    if (rule) {
      schemaFields[field.value] = rule.schema(field.value);
      maxLengthMap[field.value] = rule.maxLength;
      return;
    }
    schemaFields[field.value] = yup.string().required(`Please enter ${field.label}`);
  });

  return { schema: yup.object().shape(schemaFields), maxLengthMap };
};
